import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ProductAddonGroup {
  id: string;
  name: string;
  description: string | null;
  minSelections: number;
  maxSelections: number;
  isRequired: boolean;
  addons: ProductAddon[];
}

export interface ProductAddon {
  id: string;
  productId: string;
  name: string;
  price: number;
}

export function usePublicProductAddons(productId: string | null) {
  return useQuery({
    queryKey: ["public-product-addons", productId],
    queryFn: async (): Promise<ProductAddonGroup[]> => {
      if (!productId) return [];

      const { data, error } = await supabase.rpc("get_product_addons", {
        p_product_id: productId,
      });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      // Group the flat data by addon group
      const groupsMap = new Map<string, ProductAddonGroup>();

      for (const row of data) {
        if (!row.group_id) continue;
        
        if (!groupsMap.has(row.group_id)) {
          groupsMap.set(row.group_id, {
            id: row.group_id,
            name: row.group_name || "Adicionais",
            description: row.group_description,
            minSelections: row.min_selections ?? 0,
            maxSelections: row.max_selections ?? 5,
            isRequired: row.is_required ?? false,
            addons: [],
          });
        }

        const group = groupsMap.get(row.group_id)!;
        if (row.addon_id) {
          group.addons.push({
            id: row.addon_id,
            productId: row.addon_product_id,
            name: row.addon_name || "",
            price: Number(row.addon_price) || 0,
          });
        }
      }

      return Array.from(groupsMap.values());
    },
    enabled: !!productId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}
